import { useEffect, useRef, useState, useCallback } from "react";
import { geoOrthographic, geoPath, geoGraticule10 } from "d3-geo";
import { loadCountries, codeOf, nameOf, NON_COUNTRY, MapPlace } from "./WorldMap";
import { loadUkSubunits } from "./ukSubunits";

const VISITED = "#3aa76d";
const VISITED_LINE = "#2e8c5a";
const UNVISITED_FILL = "#fbfaf6";
const UNVISITED_LINE = "rgba(26,26,26,0.22)";

const DEG = Math.PI / 180;

// True if a lng/lat sits on the visible hemisphere for the current rotation
// (globe centre is at geographic [-rot0, -rot1]).
const isVisible = (lng: number, lat: number, rot: [number, number]): boolean => {
  const clon = -rot[0], clat = -rot[1];
  const cosd =
    Math.sin(clat * DEG) * Math.sin(lat * DEG) +
    Math.cos(clat * DEG) * Math.cos(lat * DEG) * Math.cos((lng - clon) * DEG);
  return cosd > 0;
};

// A spinnable orthographic globe rendered on canvas. Visited countries fill
// green, saved places show as gold dots; drag to rotate, otherwise it drifts.
export const Globe = ({
  visited,
  places = [],
}: {
  visited: string[];
  places?: MapPlace[];
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef = useRef<{ countries: any[]; uk: any[] } | null>(null);
  const visitedRef = useRef<Set<string>>(new Set(visited));
  const placesRef = useRef<MapPlace[]>(places);
  const rotationRef = useRef<[number, number]>([10, -25]);
  const zoomRef = useRef(1);
  const sizeRef = useRef({ w: 0, h: 0 });
  const projRef = useRef<any>(null);
  const draggingRef = useRef(false);
  const pointerOverRef = useRef(false);
  const [tip, setTip] = useState<{ name: string; x: number; y: number } | null>(null);
  const [spinning, setSpinning] = useState(true);
  const spinRef = useRef(spinning);

  visitedRef.current = new Set(visited);
  placesRef.current = places;
  spinRef.current = spinning;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const data = dataRef.current;
    if (!canvas || !data) return;
    const { w, h } = sizeRef.current;
    if (w === 0 || h === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    const R = (Math.min(w, h) / 2 - 6) * zoomRef.current;
    const projection = geoOrthographic()
      .translate([w / 2, h / 2])
      .scale(R)
      .rotate([rotationRef.current[0], rotationRef.current[1], 0])
      .clipAngle(90);
    projRef.current = projection;
    const path = geoPath(projection, ctx as any);

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    // ocean sphere
    ctx.beginPath();
    path({ type: "Sphere" } as any);
    ctx.fillStyle = "#e3eaec";
    ctx.fill();

    // graticule
    ctx.beginPath();
    path(geoGraticule10() as any);
    ctx.strokeStyle = "rgba(26,26,26,0.06)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    const drawFeat = (f: any, code: string) => {
      const on = visitedRef.current.has(code);
      ctx.beginPath();
      path(f);
      ctx.fillStyle = on ? VISITED : UNVISITED_FILL;
      ctx.fill();
      ctx.strokeStyle = on ? VISITED_LINE : UNVISITED_LINE;
      ctx.lineWidth = on ? 0.7 : 0.4;
      ctx.stroke();
    };
    data.countries.forEach((f) => drawFeat(f, codeOf(f.properties)));
    data.uk.forEach((f) => drawFeat(f, f.properties.code));

    // sphere outline
    ctx.beginPath();
    path({ type: "Sphere" } as any);
    ctx.strokeStyle = "rgba(26,26,26,0.3)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // place dots (front hemisphere only)
    const rot = rotationRef.current;
    placesRef.current.forEach((p) => {
      if (!isVisible(p.lng, p.lat, rot)) return;
      const xy = projection([p.lng, p.lat]);
      if (!xy) return;
      ctx.beginPath();
      ctx.arc(xy[0], xy[1], 4, 0, 2 * Math.PI);
      ctx.fillStyle = "#e2b31a";
      ctx.fill();
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    ctx.restore();
  }, []);

  // load geometry once
  useEffect(() => {
    let cancel = false;
    Promise.all([loadCountries(), loadUkSubunits().catch(() => null)]).then(
      ([data, uk]) => {
        if (cancel) return;
        dataRef.current = {
          countries: data.features.filter(
            (f: any) =>
              codeOf(f.properties) !== "GB" &&
              !NON_COUNTRY.has(f.properties.CONTINENT)
          ),
          uk: uk ? uk.features : [],
        };
        draw();
      }
    );
    return () => {
      cancel = true;
    };
  }, [draw]);

  // keep canvas sized to its container (hi-dpi) and redraw
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w, h };
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      draw();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  // redraw when the visited set / places change
  useEffect(() => {
    draw();
  }, [visited, places, draw]);

  // wheel to zoom (native listener so we can preventDefault the page scroll)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoomRef.current = Math.max(1, Math.min(8, zoomRef.current * factor));
      draw();
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [draw]);

  // gentle auto-rotation, paused while the pointer is over the globe
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (t: number) => {
      const dt = t - last;
      last = t;
      if (spinRef.current && !pointerOverRef.current && !draggingRef.current) {
        rotationRef.current[0] = (rotationRef.current[0] + dt * 0.004) % 360;
        draw();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [draw]);

  // ---- interaction ----
  const dragStart = useRef({ x: 0, y: 0, rot: [0, 0] as [number, number] });
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  // Track the gesture so a quick tap (no drag, no pinch) shows a place name —
  // there's no hover on touch devices.
  const gestureRef = useRef({ moved: false, pinched: false });

  const onPointerDown = (e: React.PointerEvent) => {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.target as Element).setPointerCapture(e.pointerId);
    setTip(null);
    if (pointersRef.current.size === 1) gestureRef.current = { moved: false, pinched: false };
    if (pointersRef.current.size === 2) {
      // start a pinch: remember finger distance and the zoom at that moment
      const [a, b] = Array.from(pointersRef.current.values());
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: zoomRef.current };
      gestureRef.current.pinched = true;
      draggingRef.current = false;
    } else {
      draggingRef.current = true;
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        rot: [rotationRef.current[0], rotationRef.current[1]],
      };
    }
  };

  const hitTestPlace = (e: React.PointerEvent): boolean => {
    const canvas = canvasRef.current;
    const projection = projRef.current;
    if (!canvas || !projection) return false;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const rot = rotationRef.current;
    let found: { name: string; x: number; y: number } | null = null;
    for (const p of placesRef.current) {
      if (!isVisible(p.lng, p.lat, rot)) continue;
      const xy = projection([p.lng, p.lat]);
      if (!xy) continue;
      if ((xy[0] - mx) ** 2 + (xy[1] - my) ** 2 <= 169) {
        found = { name: p.date ? `${p.name} · ${p.date}` : p.name, x: xy[0], y: xy[1] };
        break;
      }
    }
    setTip(found);
    return found !== null;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const [a, b] = Array.from(pointersRef.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      zoomRef.current = Math.max(1, Math.min(8, pinchRef.current.zoom * (dist / pinchRef.current.dist)));
      draw();
      return;
    }
    if (draggingRef.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) gestureRef.current.moved = true;
      const [r0, r1] = dragStart.current.rot;
      const nextPhi = Math.max(-89, Math.min(89, r1 - dy * 0.3));
      rotationRef.current = [r0 + dx * 0.3, nextPhi];
      draw();
    } else {
      hitTestPlace(e);
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId);
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) {
      draggingRef.current = false;
      // A clean tap (no rotate, no pinch) reveals the place name on touch.
      if (!gestureRef.current.moved && !gestureRef.current.pinched) {
        const hit = hitTestPlace(e);
        // On touch there's no hover, so pause the spin to keep the label in place.
        if (hit && e.pointerType !== "mouse") setSpinning(false);
      }
    }
  };

  return (
    <div
      className="places__globe"
      ref={wrapRef}
      onPointerEnter={() => (pointerOverRef.current = true)}
      onPointerLeave={(e) => {
        pointerOverRef.current = false;
        draggingRef.current = false;
        pointersRef.current.clear();
        pinchRef.current = null;
        // Only a real mouse-leave dismisses the label. On touch, lifting the
        // finger also fires leave — keep the tapped name until the next tap.
        if (e.pointerType === "mouse") setTip(null);
      }}
      onDoubleClick={() => {
        zoomRef.current = 1; // double-click to reset zoom
        draw();
      }}
    >
      <canvas
        ref={canvasRef}
        className="places__globe-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
      <button
        type="button"
        className="places__globe-spin"
        onClick={() => setSpinning((s) => !s)}
        title={spinning ? "Stoppa rotation" : "Starta rotation"}
      >
        {spinning ? "⏸ Stoppa" : "▶ Rotera"}
      </button>
      {tip && (
        <div className="places__globe-tip" style={{ left: tip.x, top: tip.y }}>
          {tip.name}
        </div>
      )}
    </div>
  );
};
